import { useBackendApi } from './backendApi';
import { useEventsPublisher } from 'event-bus';
import { PERSON_CREATED, PersonCreatedEvent } from './peopleService';

export interface BulkContactsImportRequest {
  inputCsv: string;
}

export interface BulkContactsImportResponse {
  fullCsv: string;
  failedCsv: string;
}

export enum ContactImportResult {
  SUCCESS = 'SUCCESS',
  REQUIRED_DATA_MISSING = 'REQUIRED_DATA_MISSING',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  UNKNOWN = 'UNKNOWN',
  SKIPPED = 'SKIPPED'
}

export interface ImportStatistics {
  total: number;
  successful: number;
  failed: number;
  skipped: number;
}

export function parseStatistics(fullCsv: string): ImportStatistics {
  const lines = fullCsv.split('\n').filter(line => line.trim().length > 0);

  if (lines.length <= 1) {
    return { total: 0, successful: 0, failed: 0, skipped: 0 };
  }

  const dataLines = lines.slice(1);
  const total = dataLines.length;
  let successful = 0;
  let failed = 0;
  let skipped = 0;

  dataLines.forEach(line => {
    const lastCommaIndex = line.lastIndexOf(',');
    const secondLastCommaIndex = line.lastIndexOf(',', lastCommaIndex - 1);

    if (secondLastCommaIndex === -1) return;

    const resultColumn = line.substring(secondLastCommaIndex + 1, lastCommaIndex).trim();

    if (resultColumn === ContactImportResult.SUCCESS) {
      successful++;
    } else if (resultColumn === ContactImportResult.SKIPPED) {
      skipped++;
    } else {
      failed++;
    }
  });

  return { total, successful, failed, skipped };
}

export function downloadCsv(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export function useBulkContactsImportService() {
  const nekstApi = useBackendApi();
  const eventsPublisher = useEventsPublisher();

  const downloadTemplate = async (): Promise<void> => {
    const csvContent = await nekstApi.get('/contacts/bulk/template.csv') as string;
    downloadCsv(csvContent, 'contacts-template.csv');
  };

  const importContacts = async (inputCsv: string): Promise<BulkContactsImportResponse> => {
    const request: BulkContactsImportRequest = { inputCsv };
    const response = await nekstApi.post('/contacts/bulk/import', request) as BulkContactsImportResponse;

    eventsPublisher.publish(PERSON_CREATED, {} as PersonCreatedEvent);

    return response;
  };

  return {
    downloadTemplate,
    importContacts,
  };
}
